import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointemploymentDetails";
import { EMPLOYMENT } from "app/constants/apiEndPoints";
import { useEmployment } from "app/hooks/employment/useEmployment";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { JointemploymentDetailsFromTwo } from "app/utils/types";
import moment from "moment";
import DynamicFormTwo from "app/components/Individual/employmentDetails/dynamicForms/DynamicFormTwo";

const PartTwo: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: submitEmployment, isPending } = useEmployment();

  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formTwo, ...others } = useSelector(
    (state: RootState) => state.jointemploymentDetails.stepsData
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    register,
    reset,
    setValue,
  } = useForm<JointemploymentDetailsFromTwo>({
    defaultValues: {
      yourDetails: {
        previousemployer: "no",
        employmentType: "yes",
        hassecondjob: "no",
      },
      coBorrowerDetails: {
        hassecondjob: "no",
        employmentType: "yes",
        previousemployer: "no",
      },
    },
  });

  const yourDetailsJoiningDate = watch("yourDetails.joiningDate");
  const coJoiningDate = watch("coBorrowerDetails.joiningDate");
  const yourHasSecondJob = watch("yourDetails.previousemployer");
  const coHasSecondJob = watch("coBorrowerDetails.previousemployer");

  const showYourEmploymentType =
    !!yourDetailsJoiningDate &&
    moment(yourDetailsJoiningDate).isBefore(moment()) &&
    moment().diff(moment(yourDetailsJoiningDate), "months") <= 12;

  const coEmploymentType =
    !!coJoiningDate &&
    moment(coJoiningDate).isBefore(moment()) &&
    moment().diff(moment(coJoiningDate), "months") <= 12;

  const onSubmit = async (data: JointemploymentDetailsFromTwo) => {
    const { yourDetails, coBorrowerDetails } = data || {};

    const yourDetailsData = {
      ...yourDetails,
      joiningDate: yourDetails?.joiningDate,
      employerName: others?.formOne?.yourDetails?.employerName ?? "",
    };

    const coBorrowerDetailsData = {
      ...coBorrowerDetails,
      joiningDate: coBorrowerDetails?.joiningDate,
      employerName: others?.formOne?.coBorrowerDetails?.employerName ?? "",
    };

    const savedYourDetailsData = {
      ...yourDetails,
      joiningDate: yourDetails?.joiningDate,
      employerName: others?.formOne?.yourDetails?.employerName ?? "",
      ...(yourDetails?.previousemployer === "yes" && {
        hassecondjob: "no",
      }),
    };
    const savedCoBorrowerDetailsData = {
      ...coBorrowerDetails,
      joiningDate: coBorrowerDetails?.joiningDate,
      employerName: others?.formOne?.coBorrowerDetails?.employerName ?? "",
      ...(coBorrowerDetails?.previousemployer === "yes" && {
        hassecondjob: "no",
      }),
    };

    if ("previousemployer" in yourDetailsData) {
      delete (yourDetailsData as Record<string, any>).previousemployer;
    }
    if ("hassecondjob" in yourDetailsData) {
      delete (yourDetailsData as Record<string, any>).hassecondjob;
    }
    if ("previousemployer" in coBorrowerDetailsData) {
      delete (coBorrowerDetailsData as Record<string, any>).previousemployer;
    }
    if ("hassecondjob" in coBorrowerDetailsData) {
      delete (coBorrowerDetailsData as Record<string, any>).hassecondjob;
    }

    const request1 = {
      url: `${EMPLOYMENT}/${savedIds?.employmentID}/second`,
      data: yourDetailsData,
    };
    const request2 = {
      url: `${EMPLOYMENT}/${savedIds?.secondEmploymentID}/second`,
      data: coBorrowerDetailsData,
    };
    const storedData = {
      yourDetails: savedYourDetailsData ?? {},
      coBorrowerDetails: savedCoBorrowerDetailsData ?? {},
    };

    const isYourPreviousemployer = yourDetails.previousemployer === "no";
    const isCoPreviousemployer = coBorrowerDetails.previousemployer === "no";

    const isyourHasSecondjob = yourDetails.hassecondjob === "no";
    const isCoHasSecondjob = coBorrowerDetails.hassecondjob === "no";

    //  ---- has second job -----
    //  ---- has second job -----

    if (
      isYourPreviousemployer &&
      isCoPreviousemployer &&
      isyourHasSecondjob &&
      isCoHasSecondjob
    ) {
      try {
        const [response1, response2] = await Promise.all([
          submitEmployment(request1),
          submitEmployment(request2),
        ]);
        if (response1?.success && response2?.success) {
          navigate(`/joint-income-details`);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Something went wrong.");
      }
    } else if (
      isYourPreviousemployer &&
      !isyourHasSecondjob &&
      isCoPreviousemployer &&
      isCoHasSecondjob
    ) {
      dispatch(setStep(3));
    } else if (
      isYourPreviousemployer &&
      isyourHasSecondjob &&
      isCoPreviousemployer &&
      !isCoHasSecondjob
    ) {
      dispatch(setStep(3));
    } else if (
      isYourPreviousemployer &&
      !isyourHasSecondjob &&
      isCoPreviousemployer &&
      !isCoHasSecondjob
    ) {
      dispatch(setStep(3));
    } else {
      dispatch(nextStep());
    }
    dispatch(
      addStepsData({
        ...others,
        formTwo: storedData,
      })
    );
  };

  useEffect(() => {
    if (
      formTwo?.yourDetails?.joiningDate ||
      formTwo?.coBorrowerDetails?.joiningDate
    ) {
      if (formTwo) {
        reset({
          yourDetails: {
            joiningDate: formTwo.yourDetails?.joiningDate ?? "",
            employmentType: formTwo.yourDetails?.employmentType ?? "yes",
            previousemployer: formTwo.yourDetails?.previousemployer ?? "no",
            hassecondjob: formTwo.yourDetails?.hassecondjob ?? "no",
          },
          coBorrowerDetails: {
            joiningDate: formTwo.coBorrowerDetails?.joiningDate ?? "",
            employmentType: formTwo.coBorrowerDetails?.employmentType ?? "yes",
            previousemployer:
              formTwo.coBorrowerDetails?.previousemployer ?? "no",
            hassecondjob: formTwo.coBorrowerDetails?.hassecondjob ?? "no",
          },
        });
      }
    }
    if (
      formTwo?.yourDetails?.hassecondjob === undefined ||
      formTwo?.coBorrowerDetails?.hassecondjob === undefined
    ) {
      reset({
        yourDetails: {
          ...formTwo?.yourDetails,
          hassecondjob: formTwo?.yourDetails?.hassecondjob ?? "no",
          previousemployer: formTwo?.yourDetails?.previousemployer ?? "no",
        },
        coBorrowerDetails: {
          ...formTwo?.coBorrowerDetails,
          hassecondjob: formTwo?.coBorrowerDetails?.hassecondjob ?? "no",
          previousemployer:
            formTwo?.coBorrowerDetails?.previousemployer ?? "no",
        },
      });
    }
  }, [formTwo, reset]);

  const typeMapping: Record<string, string> = {
    "full-time": "yes",
    "part-time": "no",
  };

  useEffect(() => {
    if (!formTwo?.yourDetails?.employmentType) return;

    setValue(
      "yourDetails.employmentType",
      typeMapping[formTwo?.yourDetails?.employmentType] ??
        formTwo?.yourDetails?.employmentType
    );
  }, [formTwo?.yourDetails?.employmentType, setValue]);

  useEffect(() => {
    if (!formTwo?.coBorrowerDetails?.employmentType) return;

    setValue(
      "coBorrowerDetails.employmentType",
      typeMapping[formTwo?.coBorrowerDetails?.employmentType] ??
        formTwo?.coBorrowerDetails?.employmentType
    );
  }, [formTwo?.coBorrowerDetails?.employmentType, setValue]);

  return (
    <>
      <ProgressBar progress={50} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Employment Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />

              <DynamicFormTwo
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                watch={watch}
                joiningDate={"yourDetails.joiningDate"}
                employmentType={"yourDetails.employmentType"}
                previousemployer={"yourDetails.previousemployer"}
                showEmploymentType={showYourEmploymentType}
                hasSecondJob={yourHasSecondJob}
                hasSecondjobName={"yourDetails.hassecondjob"}
              />
            </div>

            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <DynamicFormTwo
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                watch={watch}
                joiningDate={"coBorrowerDetails.joiningDate"}
                employmentType={"coBorrowerDetails.employmentType"}
                previousemployer={"coBorrowerDetails.previousemployer"}
                showEmploymentType={coEmploymentType}
                hasSecondJob={coHasSecondJob}
                hasSecondjobName={"coBorrowerDetails.hassecondjob"}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartTwo;
